/**
 * Base local (IndexedDB).
 *
 * Regla de oro del proyecto: **esta base es la fuente de verdad de la
 * pantalla**. La UI nunca lee de la red. El mesero toca un producto, se
 * escribe acá, la vista se repinta — y recién después, cuando haya señal,
 * el outbox empuja el cambio al servidor. Si la conexión se cae en mitad de
 * un servicio, nadie se entera.
 *
 * Dos familias de tablas:
 *
 *   • **Réplica** (`categorias`, `productos`, `mesasRemotas`): copia de solo
 *     lectura de lo que manda el servidor. Se sobrescribe en cada pull.
 *   • **Propias** (`ordenes`, `items`, `pagos`, `outbox`): las escribe esta
 *     tablet. Son las que hay que sincronizar.
 *
 * Separarlas evita el error clásico de mezclar "lo que sé del servidor" con
 * "lo que hice yo y él todavía no sabe".
 */

import Dexie, { type EntityTable } from 'dexie';
import type {
  Categoria,
  EstadoItem,
  EstadoOrden,
  EstadoMesa,
  MetodoPago,
  Producto,
  TipoOrden,
} from '../tipos/contrato';

/**
 * Puesto tal como vive en la tablet.
 *
 * No se guarda el estado que manda el servidor: el estado se deriva de las
 * cuentas locales, que siempre están más frescas. Sí se guarda `esTemporal`,
 * porque decide si el puesto desaparece de la grilla al cobrarse.
 */
export interface MesaLocal {
  id: string;
  numero: string;
  nombre: string | null;
  zona: string | null;
  capacidad: number;
  esTemporal: boolean;
  /** Estado según el servidor. Informativo: la UI usa el derivado. */
  estadoRemoto: EstadoMesa | null;
  /**
   * `true` mientras el puesto solo exista en esta tablet — se creó sin señal
   * junto con su cuenta. El pull no lo borra, y al sincronizar se reemplaza
   * por el que devuelve el servidor con su ID definitivo.
   */
  local: boolean;
}

/** Orden tal como vive en la tablet. */
export interface OrdenLocal {
  id: string;
  numero: string;
  mesaId: string | null;
  mesaNumero: string | null;
  /**
   * Texto libre para distinguir cuentas que comparten mesa: "Cuenta 1",
   * "Camisa azul", "Cumpleaños de Ana". Es lo que reemplaza a la numeración
   * física que este restaurante no tiene.
   */
  etiqueta: string | null;
  comensales: number;
  tipo: TipoOrden;
  estado: EstadoOrden;
  propinaAceptada: boolean;
  notas: string | null;
  meseroNombre: string;
  abiertaEn: number;
  /** Versión conocida del servidor, para el bloqueo optimista. */
  version: number;
  /** `false` mientras la orden no haya sido confirmada por el backend. */
  sincronizada: boolean;
}

export interface ItemLocal {
  id: string;
  ordenId: string;
  productoId: string;
  nombreSnap: string;
  /** Precio congelado al momento de tomar el pedido, igual que en el backend. */
  precioUnitario: number;
  cantidad: number;
  notas: string | null;
  estado: EstadoItem;
  creadoEn: number;
  anulado: boolean;
}

export interface PagoLocal {
  id: string;
  ordenId: string;
  /** Turno al que pertenece el cobro. Sin esto el arqueo no puede agruparse. */
  turnoId: string;
  /** El valor exacto que acepta el CHECK de `pagos` en Postgres. */
  metodo: MetodoPago;
  /** Etiqueta para el recibo: 'Nequi', 'Tarjeta débito', 'Efectivo'. */
  detalle: string | null;
  referencia: string | null;

  /** Lo cobrado en ESTE movimiento. En pago mixto es una parte del total. */
  monto: number;
  /** Total de la orden al momento del cobro. Congelado. */
  total: number;
  subtotal: number;
  propina: number;
  recibido: number;
  cambio: number;

  creadoEn: number;
  /** Snapshot de los ítems: el recibo debe poder reimprimirse años después. */
  itemsSnap: { nombre: string; cantidad: number; precio: number }[];
  mesaNumero: string | null;
  ordenNumero: string;
  meseroNombre: string;
  minutos: number;
  /** Pasa a `true` cuando el backend confirma el cobro. */
  sincronizado: boolean;
  anuladoEn: number | null;
}
/** Una mutación esperando su turno para viajar al servidor. */
export interface OperacionOutbox {
  id?: number;
  /** Qué hacer. El pusher despacha por este campo. */
  tipo:
    | 'orden.abrir'
    | 'orden.item.agregar'
    | 'orden.item.actualizar'
    | 'orden.item.anular'
    | 'orden.propina'
    | 'orden.cuenta'
    | 'orden.pago'
    | 'orden.renombrar'
    | 'orden.mover'
    | 'orden.descartar';
  ordenId: string;
  carga: Record<string, unknown>;
  creadaEn: number;
  intentos: number;
  proximoIntentoEn: number;
  ultimoError: string | null;
  /**
   * `true` cuando la operación se agotó: el servidor la rechazó de forma
   * definitiva (4xx) o se acabaron los reintentos. El pusher la salta y deja
   * de gastar batería contra algo que no va a cambiar.
   */
  bloqueada: boolean;
}

/** Pares clave/valor sueltos: cursores de sync, sesión, preferencias. */
export interface Meta {
  clave: string;
  valor: unknown;
}

class BaseLocal extends Dexie {
  categorias!: EntityTable<Categoria, 'id'>;
  productos!: EntityTable<Producto, 'id'>;
  mesasRemotas!: EntityTable<MesaLocal, 'id'>;
  ordenes!: EntityTable<OrdenLocal, 'id'>;
  items!: EntityTable<ItemLocal, 'id'>;
  pagos!: EntityTable<PagoLocal, 'id'>;
  outbox!: EntityTable<OperacionOutbox, 'id'>;
  meta!: EntityTable<Meta, 'clave'>;

  constructor() {
    super('pos_lordaen');

    // Ojo con los índices: IndexedDB no acepta booleanos como clave, así que
    // `disponible`, `anulado`, `sincronizada` y `bloqueada` NO se indexan —
    // se filtran en memoria. Son colecciones de decenas de filas, no de miles.
    this.version(1).stores({
      categorias: 'id, orden',
      productos: 'id, categoriaId, nombre',
      mesasRemotas: 'id, numero, estado',
      ordenes: 'id, mesaId, estado, abiertaEn',
      items: 'id, ordenId, productoId',
      pagos: 'id, ordenId, creadoEn',
      outbox: '++id, ordenId, proximoIntentoEn',
      meta: 'clave',
    });

    // v2 — puestos dinámicos y varias cuentas por mesa (migración 0003 del
    // backend). Se rellenan los campos nuevos en vez de borrar la base: una
    // tablet puede tener cuentas sin sincronizar al momento de actualizar.
    this.version(2)
      .stores({ mesasRemotas: 'id, numero' })
      .upgrade(async (tx) => {
        await tx
          .table<MesaLocal>('mesasRemotas')
          .toCollection()
          .modify((m) => {
            m.esTemporal ??= false;
            m.local ??= false;
            m.estadoRemoto ??= null;
          });
        await tx
          .table<OrdenLocal>('ordenes')
          .toCollection()
          .modify((o) => {
            o.etiqueta ??= null;
          });
      });

    // v3 — índice por turno en los pagos. Lo necesita el resumen de caja, que
    // se calcula desde IndexedDB para que el total no vaya una venta atrás.
    // Los pagos anteriores no tienen `turnoId`; se les asigna cadena vacía
    // para que el índice no los rechace, y quedan fuera del resumen (son de
    // turnos ya cerrados, así que no molestan).
    this.version(3)
      .stores({ pagos: 'id, ordenId, turnoId, creadoEn' })
      .upgrade(async (tx) => {
        await tx
          .table<PagoLocal>('pagos')
          .toCollection()
          .modify((p) => {
            p.turnoId ??= '';
            p.monto ??= p.total ?? 0;
            p.anuladoEn ??= null;
          });
      });
  }
}

export const db = new BaseLocal();

/* ────────────────────────────── Helpers meta ──────────────────────────── */

export async function leerMeta<T>(clave: string, porDefecto: T): Promise<T> {
  const fila = await db.meta.get(clave);
  return fila ? (fila.valor as T) : porDefecto;
}

export const guardarMeta = (clave: string, valor: unknown) =>
  db.meta.put({ clave, valor });

/** Borra todo lo local. Se usa al cerrar sesión o al cambiar de restaurante. */
export async function limpiarTodo(): Promise<void> {
  await db.transaction(
    'rw',
    [db.categorias, db.productos, db.mesasRemotas, db.ordenes, db.items, db.pagos, db.outbox, db.meta],
    async () => {
      await Promise.all([
        db.categorias.clear(),
        db.productos.clear(),
        db.mesasRemotas.clear(),
        db.ordenes.clear(),
        db.items.clear(),
        db.pagos.clear(),
        db.outbox.clear(),
        db.meta.clear(),
      ]);
    },
  );
}
